import { useLoaderData, useSubmit } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'

import { HEADER_HEIGHT } from '../root/components/Header'
import { LoaderData } from '../../types'
import { getOption, useDomainsDataOptions, useThemesDataOptions, searchString } from '../../utils'
import { ProfileSchema } from './Profile.schema'
import { profileLoader } from './Profile.loader'

const Background = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.secondaryBackground,
  height: `calc(100% - ${HEADER_HEIGHT} - 1px)`,
  width: '100%',
}))
const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  background: theme.palette.background.default,
  width: '100%',
  height: '100%',
  padding: ['1rem', '2rem'],
  overflowY: 'auto',
}))

export default function Profile() {
  const { t } = useTranslation('profile')
  const submit = useSubmit()

  const currentUser = useLoaderData() as LoaderData<ReturnType<typeof profileLoader>>
  const domainsOptions = useDomainsDataOptions()
  const themesOptions = useThemesDataOptions()

  const { control, getValues, handleSubmit } = useForm<ProfileSchema>({
    defaultValues: {
      domains: currentUser?.domains ?? [],
      themes: currentUser?.themes ?? [],
    },
    resolver: zodResolver(ProfileSchema),
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => {
    event.preventDefault()
    const values = getValues()

    try {
      const data = ProfileSchema.safeParse(values)

      if ('error' in data && !data.success) {
        toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
        data.error.issues.forEach(issue => {
          toast.warn(`${issue.path.toString()}: ${issue.message}`, {
            autoClose: 60000,
          })
        })
        console.error(data.error)
      }
      if ('data' in data && data.success) {
        submit(data.data, {
          method: 'POST',
          encType: 'application/json',
          action: `/profile`,
        })
      }
    } catch (e) {
      console.error(e)
      toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
      toast.warn(t('Erreur innatendue', { ns: 'common' }))
    }
  }

  return (
    <Background>
      <Form onSubmit={onSubmitForm}>
        <Stack
          sx={theme => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: '0.5rem',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.boxShadows.main,
          })}
          padding="1rem"
          height="inherit"
          overflow="auto"
          gap="1rem"
        >
          <Typography variant="h1">{t('Profil')}</Typography>
          <Divider sx={{ margin: 0 }} />

          <Stack direction="row" flex="1 0 auto" gap="1rem">
            <Stack flex="1 0 0%" gap="1rem">
              <Typography variant="h3">{t('Informations personnelles')} :</Typography>
              <Typography variant="body1" fontWeight="600">
                {t('Prénom')}: {currentUser?.first_name}
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {t('Nom')}: {currentUser?.last_name}
              </Typography>
            </Stack>

            <Divider orientation="vertical" sx={{ margin: 0 }} />

            <Stack flex="1 0 0%" gap="1rem">
              <Typography variant="h3">{t('Compétences')} :</Typography>
              <Controller
                control={control}
                name="domains"
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    onChange={(_, value) => field.onChange(value ? value.map(el => el?.value) : [])}
                    onBlur={field.onBlur}
                    defaultValue={field.value.map(val => getOption(domainsOptions, null, val))}
                    multiple
                    options={domainsOptions}
                    id="domain-autocomplete"
                    disablePortal
                    fullWidth
                    renderInput={params => (
                      <TextField
                        {...params}
                        name={field.name}
                        disabled={field.disabled}
                        label={t('Domaines')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        required
                      />
                    )}
                  />
                )}
              />

              <Controller
                control={control}
                name="themes"
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    onChange={(_, value) => field.onChange(value ? value.map(el => el?.value) : [])}
                    onBlur={field.onBlur}
                    defaultValue={field.value
                      .map(val => getOption(themesOptions, null, val))
                      .filter(Boolean)}
                    options={themesOptions}
                    multiple
                    // Filter options when the user types. It should look for the theme and the sub-theme (label and group field)
                    filterOptions={(options, state) =>
                      options.filter(
                        option =>
                          option &&
                          (searchString(option.label, state.inputValue) ||
                            searchString(option.group, state.inputValue))
                      )
                    }
                    id="theme-autocomplete"
                    groupBy={option => option?.group ?? ''}
                    disablePortal
                    fullWidth
                    renderInput={params => (
                      <TextField
                        {...params}
                        name={field.name}
                        disabled={field.disabled}
                        label={t('Thèmes et sous-thèmes')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        required
                      />
                    )}
                  />
                )}
              />

              <Stack flex="1 1 auto" direction="column" />

              <Stack direction="row" gap="1rem" justifyContent="flex-end">
                <Button
                  onClick={e => {
                    void handleSubmit(
                      () => {
                        onSubmitForm(e)
                      },
                      e => {
                        console.error(e)
                        toast.error(t('Veuillez corriger les erreurs'))
                      }
                    )()
                  }}
                  variant="contained"
                >
                  {t('Enregistrer')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Form>
    </Background>
  )
}
